// src/components/StudentLoanHelp.js
import React from 'react';
import { Card, Typography, Collapse, List, Space, Tag } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { TAX_YEARS } from '../constants/taxConstants';

const { Text, Link, Title } = Typography;
const { Panel } = Collapse;

const StudentLoanHelp = ({ selectedTaxYear = "2024/2025" }) => {
  // Get the student loan threshold details for the selected tax year
  const getLoanThresholds = () => {
    const taxYearData = TAX_YEARS[selectedTaxYear];
    if (!taxYearData || !taxYearData.STUDENT_LOANS) {
      return {
        PLAN_1: "£22,095",
        PLAN_2: "£27,295",
        PLAN_4: "£31,395",
        PLAN_5: "£25,000",
        POSTGRAD: "£21,000"
      };
    }
    
    // Format the thresholds as currency
    const formatCurrency = (value) => {
      return new Intl.NumberFormat('en-GB', { 
        style: 'currency', 
        currency: 'GBP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    };
    
    return {
      PLAN_1: formatCurrency(taxYearData.STUDENT_LOANS.PLAN_1.THRESHOLD),
      PLAN_2: formatCurrency(taxYearData.STUDENT_LOANS.PLAN_2.THRESHOLD),
      PLAN_4: formatCurrency(taxYearData.STUDENT_LOANS.PLAN_4.THRESHOLD),
      PLAN_5: formatCurrency(taxYearData.STUDENT_LOANS.PLAN_5.THRESHOLD),
      POSTGRAD: formatCurrency(taxYearData.STUDENT_LOANS.POSTGRAD.THRESHOLD)
    };
  };
  
  const thresholds = getLoanThresholds();
  
  return (
    <Collapse ghost>
      <Panel 
        header={
          <Space>
            <InfoCircleOutlined />
            <Text strong>How to determine your Student Loan Plan</Text>
          </Space>
        }
        key="1"
      >
        <Card bordered={false} size="small">
          <Title level={5}>Student Loan Plans Explained</Title>
          
          <Collapse ghost>
            <Panel header="Plan 1" key="plan1">
              <Text>You're on Plan 1 if:</Text>
              <List
                size="small"
                bordered={false}
                split={false}
                dataSource={[
                  'You started your undergraduate course before September 1, 2012 in England or Wales', 
                  'You studied in Northern Ireland',
                  'You studied in Scotland and applied through the Student Awards Agency Scotland (SAAS)'
                ]}
                renderItem={(item) => <List.Item>{item}</List.Item>}
              />
              <Text type="secondary">Repayment threshold: {thresholds.PLAN_1} per year ({selectedTaxYear})</Text>
            </Panel>
            
            <Panel header="Plan 2" key="plan2">
              <Text>You're on Plan 2 if:</Text>
              <List
                size="small"
                bordered={false}
                split={false}
                dataSource={[
                  'You started your undergraduate course on or after September 1, 2012 in England or Wales',
                  "You're an EU student who started an undergraduate course in England or Wales on or after September 1, 2012"
                ]}
                renderItem={(item) => <List.Item>{item}</List.Item>}
              />
              <Text type="secondary">Repayment threshold: {thresholds.PLAN_2} per year ({selectedTaxYear})</Text>
            </Panel>
            
            <Panel header="Plan 4" key="plan4">
              <Text>You're on Plan 4 if:</Text>
              <List
                size="small"
                bordered={false}
                split={false}
                dataSource={[
                  'You took out a student loan in Scotland on or after 1 September 1998'
                ]}
                renderItem={(item) => <List.Item>{item}</List.Item>}
              />
              <Text type="secondary">Repayment threshold: {thresholds.PLAN_4} per year ({selectedTaxYear})</Text>
            </Panel>
            
            <Panel header="Plan 5" key="plan5">
              <Text>You're on Plan 5 if:</Text>
              <List
                size="small"
                bordered={false}
                split={false}
                dataSource={[
                  'You started an undergraduate course on or after 1 August 2023 in England'
                ]}
                renderItem={(item) => <List.Item>{item}</List.Item>}
              />
              <Text type="secondary">Repayment threshold: {thresholds.PLAN_5} per year ({selectedTaxYear})</Text>
            </Panel>
            
            <Panel header="Postgraduate Loan" key="postgrad">
              <Text>You have a Postgraduate Loan if:</Text>
              <List
                size="small"
                bordered={false}
                split={false}
                dataSource={[
                  "You took out a Postgraduate Master's Loan in England or Wales",
                  'You took out a Postgraduate Doctoral Loan in England or Wales',
                  'You took out a Postgraduate Tuition Fee Loan or Postgraduate Living Cost Loan in Scotland',
                  'You took out a Postgraduate Tuition Fee Loan in Northern Ireland'
                ]}
                renderItem={(item) => <List.Item>{item}</List.Item>}
              />
              <Text type="secondary">Repayment threshold: {thresholds.POSTGRAD} per year ({selectedTaxYear})</Text>
              <br />
              <Text type="secondary">Note: Postgraduate Loans are repaid at 6%, unlike other plans which are repaid at 9%</Text>
            </Panel>
          </Collapse>
          
          <Text type="secondary" style={{ marginTop: 16, display: 'block' }}>
            You can have multiple plans at the same time. If you have Plans 1, 2, 4, or 5, you'll only make repayments according to the plan with the lowest threshold. If you have a Postgraduate Loan as well, you'll make repayments on that too.
          </Text>
          
          <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
            For more detailed information, visit the <Link href="https://www.gov.uk/repaying-your-student-loan" target="_blank">official UK government guidance</Link>.
          </Text>
        </Card>
      </Panel>
    </Collapse>
  );
};

export default StudentLoanHelp;